/*
 * Diagnostic Sécurité Maison - Securidou
 * Calcul 100% côté client (vanilla, aucune dépendance, aucun AJAX).
 * Lit les réponses dans le DOM, calcule un score de sécurité, liste les
 * risques restants et révèle les produits recommandés (rendus en Liquid).
 *
 * Conventions : préfixe CSS sd-tool-, data-attributes pour le pilotage.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'sd_diagnostic_v1';

  // Facteur de priorité selon le stade moteur de bébé (plus mobile = risque plus urgent).
  var STAGE_FACTOR = { assis: 1, rampe: 1.3, debout: 1.6, marche: 2 };

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  ready(function () {
    var root = document.querySelector('[data-diagnostic]');
    if (!root) return;

    var form = root.querySelector('[data-diagnostic-form]');
    var resultBox = root.querySelector('[data-result]');
    var scoreEl = root.querySelector('[data-score]');
    var gaugeEl = root.querySelector('[data-gauge]');
    var verdictEl = root.querySelector('[data-verdict]');
    var riskListEl = root.querySelector('[data-risk-list]');
    var recosEl = root.querySelector('[data-recos]');
    var emptyRecosEl = root.querySelector('[data-recos-empty]');
    var progressEl = root.querySelector('[data-progress]');
    var pool = root.querySelector('[data-product-pool]');
    var shareBtn = root.querySelector('[data-share]');
    var resetBtn = root.querySelector('[data-reset]');

    if (!form || !resultBox) return;

    var items = Array.prototype.slice.call(form.querySelectorAll('[data-item]'));
    var stageInputs = Array.prototype.slice.call(form.querySelectorAll('input[name="sd-stage"]'));

    /* ---------- Persistance (localStorage) ---------- */
    function saveState() {
      try {
        var state = { answers: {}, stage: getStage() };
        items.forEach(function (item) {
          var key = item.getAttribute('data-item');
          var checked = item.querySelector('input[type="radio"]:checked');
          if (checked) state.answers[key] = checked.value;
        });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        /* stockage indisponible - on ignore silencieusement */
      }
    }

    function restoreState() {
      var raw;
      try {
        raw = localStorage.getItem(STORAGE_KEY);
      } catch (e) {
        return;
      }
      if (!raw) return;
      var state;
      try {
        state = JSON.parse(raw);
      } catch (e) {
        return;
      }
      if (state.stage) {
        var stageInput = form.querySelector('input[name="sd-stage"][value="' + state.stage + '"]');
        if (stageInput) stageInput.checked = true;
      }
      if (state.answers) {
        Object.keys(state.answers).forEach(function (key) {
          var item = form.querySelector('[data-item="' + key + '"]');
          if (!item) return;
          var input = item.querySelector('input[value="' + state.answers[key] + '"]');
          if (input) input.checked = true;
        });
      }
    }

    function getStage() {
      var checked = form.querySelector('input[name="sd-stage"]:checked');
      return checked ? checked.value : 'rampe';
    }

    /* ---------- Progression ---------- */
    function updateProgress() {
      if (!progressEl) return;
      var answered = items.filter(function (item) {
        return item.querySelector('input[type="radio"]:checked');
      }).length;
      var pct = items.length ? Math.round((answered / items.length) * 100) : 0;
      progressEl.style.setProperty('--sd-progress', pct + '%');
      progressEl.setAttribute('aria-valuenow', String(pct));
      var label = progressEl.querySelector('[data-progress-label]');
      if (label) label.textContent = answered + ' / ' + items.length + ' répondu';
    }

    /* ---------- Calcul du score ---------- */
    function compute() {
      var stage = getStage();
      var factor = STAGE_FACTOR[stage] || 1;
      var secured = 0;
      var atRisk = 0;
      var risks = [];
      var handles = [];

      items.forEach(function (item) {
        var checked = item.querySelector('input[type="radio"]:checked');
        if (!checked) return; // non répondu → ignoré
        var value = checked.value;
        if (value === 'na') return; // non concerné → exclu du score
        if (value === 'oui') {
          secured += 1;
          return;
        }
        // value === 'non' → risque restant
        atRisk += 1;
        var gravity = parseFloat(item.getAttribute('data-gravity')) || 1;
        risks.push({
          label: item.getAttribute('data-label') || '',
          risk: item.getAttribute('data-risk') || item.getAttribute('data-label') || '',
          room: item.getAttribute('data-room') || '',
          product: item.getAttribute('data-product') || '',
          priority: gravity * factor
        });
        var handle = item.getAttribute('data-product');
        if (handle && handles.indexOf(handle) === -1) handles.push(handle);
      });

      var denom = secured + atRisk;
      var score = denom > 0 ? Math.round((secured / denom) * 100) : 0;
      risks.sort(function (a, b) {
        return b.priority - a.priority;
      });
      // produits triés par priorité du premier risque associé
      handles.sort(function (a, b) {
        var pa = maxPriorityForHandle(risks, a);
        var pb = maxPriorityForHandle(risks, b);
        return pb - pa;
      });

      return { score: score, answered: denom, risks: risks, handles: handles, stage: stage };
    }

    function maxPriorityForHandle(risks, handle) {
      var max = 0;
      risks.forEach(function (r) {
        if (r.product === handle && r.priority > max) max = r.priority;
      });
      return max;
    }

    function verdictFor(score, answered) {
      if (answered === 0) {
        return 'Répondez à au moins une question pour obtenir votre score.';
      }
      if (score >= 85) {
        return 'Excellent ! Votre maison est déjà très bien sécurisée pour votre bébé. Quelques détails à parfaire.';
      }
      if (score >= 60) {
        return 'Bonne base. Plusieurs zones sont sécurisées, mais certains risques prioritaires restent à traiter.';
      }
      if (score >= 35) {
        return 'Votre maison présente plusieurs risques à sécuriser en priorité pour accompagner les progrès de votre bébé.';
      }
      return 'De nombreux dispositifs de sécurité manquent encore. Voici par où commencer en priorité.';
    }

    /* ---------- Rendu du résultat ---------- */
    function render(data) {
      if (scoreEl) scoreEl.textContent = data.score;
      if (gaugeEl) gaugeEl.style.setProperty('--sd-score', data.score);
      if (verdictEl) verdictEl.textContent = verdictFor(data.score, data.answered);

      // Liste des risques restants
      if (riskListEl) {
        riskListEl.innerHTML = '';
        if (data.risks.length === 0 && data.answered > 0) {
          var li = document.createElement('li');
          li.className = 'sd-tool__risk sd-tool__risk--none';
          li.textContent = 'Aucun risque restant détecté parmi vos réponses. Félicitations !';
          riskListEl.appendChild(li);
        } else {
          data.risks.forEach(function (r, i) {
            var li = document.createElement('li');
            li.className = 'sd-tool__risk';
            var prio = i < 2 ? '<span class="sd-tool__risk__flag">Priorité</span>' : '';
            li.innerHTML =
              '<div class="sd-tool__risk__head">' + prio +
              '<span class="sd-tool__risk__room">' + escapeHtml(r.room) + '</span></div>' +
              '<span class="sd-tool__risk__label">' + escapeHtml(r.risk || r.label) + '</span>';
            riskListEl.appendChild(li);
          });
        }
      }

      // Produits recommandés : on révèle les cartes du pool correspondant aux handles
      if (recosEl && pool) {
        recosEl.innerHTML = '';
        data.handles.forEach(function (handle) {
          var card = pool.querySelector('[data-handle="' + cssEscape(handle) + '"]');
          if (card) {
            var clone = card.cloneNode(true);
            clone.hidden = false;
            recosEl.appendChild(clone);
          }
        });
        if (emptyRecosEl) {
          emptyRecosEl.hidden = data.handles.length !== 0;
        }
      }

      resultBox.hidden = false;
      if (shareBtn) shareBtn.dataset.score = data.score;

      // dataLayer (GA4/GTM) - pas de PII
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'tool_complete',
          tool: 'diagnostic-securite-maison',
          score: data.score,
          risks_count: data.risks.length,
          stage: data.stage
        });
      } catch (e) {
        /* noop */
      }

      // Focus accessible sur le résultat
      if (resultBox.setAttribute) resultBox.setAttribute('tabindex', '-1');
      resultBox.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      try {
        resultBox.focus({ preventScroll: true });
      } catch (e) {
        /* noop */
      }
    }

    /* ---------- Utilitaires ---------- */
    function escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    function cssEscape(str) {
      if (window.CSS && window.CSS.escape) return window.CSS.escape(str);
      return String(str).replace(/["\\\]]/g, '\\$&');
    }

    function prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* ---------- Événements ---------- */
    form.addEventListener('change', function () {
      updateProgress();
      saveState();
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      render(compute());
    });

    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (err) {
          /* noop */
        }
        form.reset();
        resultBox.hidden = true;
        updateProgress();
        form.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      });
    }

    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        var score = shareBtn.dataset.score || '0';
        var text = 'Ma maison est sécurisée à ' + score + '% pour mon bébé (diagnostic Securidou).';
        var url = window.location.href.split('#')[0];
        if (navigator.share) {
          navigator.share({ title: 'Diagnostic Sécurité Maison', text: text, url: url }).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(text + ' ' + url).then(function () {
            var original = shareBtn.textContent;
            shareBtn.textContent = 'Copié ✓';
            setTimeout(function () {
              shareBtn.textContent = original;
            }, 2000);
          });
        }
      });
    }

    /* ---------- Init ---------- */
    restoreState();
    updateProgress();
  });
})();
